import { useMemo, useState } from 'react'
import { Pencil, Plus, TreePalm } from 'lucide-react'
import { differenceInCalendarDays, format, parseISO } from 'date-fns'

import { PageShell } from '@/components/common/PageShell'
import { EmptyState } from '@/components/common/EmptyState'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  DataTable,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCard,
} from '@/components/common/DataTable'
import { Badge, Button, Select } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption, BadgeVariant } from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import { useMyCompanyFilter } from '@/features/companies/hooks/useMyCompanyFilter'
import { useMyLeaves } from '../hooks/useMyLeaves'
import { CreateLeaveModal } from './CreateLeaveModal'
import type { LeaveDocument, LeaveStatus } from '../types/leave.types'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { useOpenFromQuery } from '@/hooks/useOpenFromQuery'

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'pending_admin', label: 'Pending admin' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
]

const STATUS_VARIANT: Record<LeaveStatus, BadgeVariant> = {
  pending: 'warning',
  pending_admin: 'warning',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
}

const STATUS_LABEL: Record<LeaveStatus, string> = {
  pending: 'Pending',
  pending_admin: 'Pending admin',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

const LIMIT = 10

function leaveDuration(startDate: string, endDate: string, durationType: 'full' | 'half'): string {
  const days = differenceInCalendarDays(parseISO(endDate), parseISO(startDate)) + 1
  if (durationType === 'half') return '½ day'
  return days === 1 ? '1 day' : `${days} days`
}

function titleCase(s: string): string {
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function MyLeavesPage(): React.ReactElement {
  const { can } = usePermissions()
  const [status, setStatus] = useState<string>('')
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)
  useOpenFromQuery(setCreateOpen)
  const [editingLeave, setEditingLeave] = useState<LeaveDocument | null>(null)
  const companyFilter = useMyCompanyFilter({ onChange: () => setPage(1) })
  const { companies: myCompanies, selectedCompanyIds, companyIds, companyNameById } = companyFilter

  const canCreateLeave = can('leave', 'create_self')
  // The leave request endpoint is company-scoped, so we need exactly one target
  // company. Use the single selected chip, or the only company the user belongs to.
  const selectedArr = companyIds ?? []
  const createCompanyId =
    selectedArr.length === 1 ? selectedArr[0] : myCompanies.length === 1 ? myCompanies[0]._id : null

  const params = useMemo(
    () => ({
      ...(companyIds ? { companyIds } : {}),
      ...(status ? { status: status as LeaveStatus } : {}),
      page,
      limit: LIMIT,
    }),
    [companyIds, status, page]
  )

  const { data, isLoading, isError, error, refetch } = useMyLeaves(params)
  const records = data?.data ?? []
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  const onChangeFilter =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v)
      setPage(1)
    }

  return (
    <>
      <PageShell
        breadcrumbItems={[{ label: 'My Workspace', to: '/dashboard' }, { label: 'My Leaves' }]}
        icon={TreePalm}
        title="My Leaves"
        description="Leave requests across every company you belong to."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar
            filter={
              <CompanyChips
                companies={myCompanies}
                selectedCompanyIds={selectedCompanyIds}
                onToggle={companyFilter.toggle}
              />
            }
            actions={
              canCreateLeave ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setCreateOpen(true)}
                  disabled={!createCompanyId}
                  title={
                    createCompanyId
                      ? 'Request leave'
                      : 'Select a single company first to request leave'
                  }
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span className="ml-1.5">Request leave</span>
                </Button>
              ) : null
            }
          >
            <div className="w-full sm:w-45">
              <Select
                options={STATUS_OPTIONS}
                value={status}
                onChange={onChangeFilter(setStatus)}
              />
            </div>
          </WorkspaceToolbar>

          {isLoading ? (
            <DataTableCard>
              <SectionLoader variant="table" cols={7} rows={6} label="Loading leave requests" />
            </DataTableCard>
          ) : isError ? (
            <DataTableCard>
              <QueryErrorState
                error={error}
                title="Couldn't load your leave requests"
                onRetry={refetch}
                className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
              />
            </DataTableCard>
          ) : records.length === 0 ? (
            <EmptyState
              icon={TreePalm}
              title="No leave requests"
              description="Your leave requests will appear here once submitted."
            />
          ) : (
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeadCell>Type</DataTableHeadCell>
                  <DataTableHeadCell>Company</DataTableHeadCell>
                  <DataTableHeadCell>Start</DataTableHeadCell>
                  <DataTableHeadCell>End</DataTableHeadCell>
                  <DataTableHeadCell>Duration</DataTableHeadCell>
                  <DataTableHeadCell>Status</DataTableHeadCell>
                  <DataTableHeadCell>Requested</DataTableHeadCell>
                  <DataTableHeadCell className="text-right">Actions</DataTableHeadCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {records.map((l) => {
                  const cName = companyNameById.get(l.companyId) ?? '—'
                  return (
                    <DataTableRow key={l._id}>
                      <DataTableCell>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {titleCase(l.leaveType)}
                        </span>
                      </DataTableCell>
                      <DataTableCell>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{cName}</span>
                      </DataTableCell>
                      <DataTableCell>{format(parseISO(l.startDate), 'd MMM yyyy')}</DataTableCell>
                      <DataTableCell>{format(parseISO(l.endDate), 'd MMM yyyy')}</DataTableCell>
                      <DataTableCell>
                        {leaveDuration(l.startDate, l.endDate, l.durationType)}
                      </DataTableCell>
                      <DataTableCell>
                        <Badge variant={STATUS_VARIANT[l.status]}>{STATUS_LABEL[l.status]}</Badge>
                      </DataTableCell>
                      <DataTableCell>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {format(parseISO(l.createdAt), 'd MMM yyyy')}
                        </span>
                      </DataTableCell>
                      <DataTableCell className="text-right">
                        {l.status === 'pending' ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingLeave(l)}
                            title="Edit leave request"
                          >
                            <Pencil className="h-4 w-4" aria-hidden />
                          </Button>
                        ) : null}
                      </DataTableCell>
                    </DataTableRow>
                  )
                })}
              </DataTableBody>
            </DataTable>
          )}

          <TablePaginationBar
            page={page}
            limit={LIMIT}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      </PageShell>

      {createCompanyId && (
        <CreateLeaveModal
          companyId={createCompanyId}
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}
      {editingLeave && (
        <CreateLeaveModal
          companyId={editingLeave.companyId}
          open={!!editingLeave}
          leave={editingLeave}
          onClose={() => setEditingLeave(null)}
        />
      )}
    </>
  )
}

MyLeavesPage.displayName = 'MyLeavesPage'
